import styled from '@emotion/styled'
import { useEffect, useState } from 'react'

import { LoadingSpinner, SimpleTabStrip, Space } from '../../components/core-v2'
import { StepTab } from '../../components/core-v2/molecules/step-tab/step-tab'
import { TabContent } from '../../components/core-v2/molecules/tab-content'
import {
  SetEconomics,
  TradeActivate,
  TradeApprove,
  TradeCommitment,
  TradeSettlement,
  TradeSubmit
} from '../trade-approval'
import { FundingSource } from '../tradebuilder-v2/modules/02-terms/state/terms-slice'
import { useAppSelector } from '../tradebuilder-v2/store/hooks'
import { TradeActivationStep } from '../tradebuilder-v2/types'

export type ActivationProcessTab =
  | 'submit'
  | 'set-economics'
  | 'approve'
  | 'commitment'
  | 'settlement'
  | 'activate'
  | undefined

const TradeActivationLayout = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [selectedStep, setSelectedStep] = useState<TradeActivationStep>(undefined)
  const [completedStep, setCompletedStep] = useState<TradeActivationStep>(undefined)
  const builderState = useAppSelector(state => state.builder)
  const termsState = useAppSelector(state => state.terms)
  const requiresSettlement = termsState && termsState.terms.requiresSettlement
  const { requiresFullApproval } = builderState

  useEffect(() => {
    setIsLoading(false)
    const currentStep = builderState.status
    if (currentStep > TradeActivationStep.activate) {
      setSelectedStep(TradeActivationStep.activate)
    } else if (
      currentStep === TradeActivationStep.settlement &&
      termsState.terms.fundingSource === FundingSource.Switch
    ) {
      setSelectedStep(TradeActivationStep.activate)
    } else {
      setSelectedStep(currentStep)
    }
    setCompletedStep(currentStep)
  }, [
    builderState.accountId,
    builderState.status,
    requiresFullApproval,
    termsState.terms.fundingSource
  ])
  useEffect(() => {
    if (builderState.selectedAccountId !== builderState.accountId) {
      setIsLoading(true)
      setCompletedStep(TradeActivationStep.unDef)
      setSelectedStep(TradeActivationStep.unDef)
    }
  }, [builderState.accountId, builderState.selectedAccountId])

  const getState = (currentTabPosition: number) => {
    // Slight hack to map clientcommitmnet and commitment as same state for top level tab
    if (currentTabPosition === TradeActivationStep.clientCommitment) {
      currentTabPosition = TradeActivationStep.commitment
    }
    if (currentTabPosition === completedStep) return 'current'
    const isCompleted = currentTabPosition <= completedStep
    return isCompleted ? 'complete' : 'pending'
  }

  const renderTab = (
    stepNumber: number,
    tradeActivationStep: TradeActivationStep,
    stepName: string
  ) => (
    <StepTab
      isSelected={selectedStep === tradeActivationStep}
      state={getState(tradeActivationStep)}
      stepNumber={stepNumber}
      onClick={() => setSelectedStep(tradeActivationStep)}
    >
      {stepName}
    </StepTab>
  )

  const StyledTabs = styled.div`
    border-bottom: solid 1px var(--White90);
  `

  return (
    <Space px={5}>
      <Space mt={6}>
        <StyledTabs>
          <SimpleTabStrip>
            {renderTab(1, TradeActivationStep.submit, 'Submit')}
            {requiresFullApproval &&
              renderTab(2, TradeActivationStep.setEconomics, 'Set economics')}
            {requiresFullApproval && renderTab(3, TradeActivationStep.approve, 'Approve')}
            {renderTab(requiresFullApproval ? 4 : 2, TradeActivationStep.commitment, 'Commitment')}
            {requiresFullApproval &&
              requiresSettlement &&
              renderTab(5, TradeActivationStep.settlement, 'Settlement')}
            {requiresFullApproval &&
              renderTab(requiresSettlement ? 6 : 5, TradeActivationStep.activate, 'Activate')}
            {!requiresFullApproval &&
              termsState.terms.fundingSource === FundingSource.Switch &&
              renderTab(3, TradeActivationStep.activate, 'Activate')}
          </SimpleTabStrip>
        </StyledTabs>
      </Space>
      <TabContent>
        <LoadingSpinner contextName="activation status" show={isLoading} />
        {selectedStep === TradeActivationStep.submit && <TradeSubmit />}
        {selectedStep === TradeActivationStep.setEconomics && <SetEconomics />}
        {selectedStep === TradeActivationStep.approve && <TradeApprove />}
        {(selectedStep === TradeActivationStep.commitment ||
          selectedStep === TradeActivationStep.clientCommitment) && <TradeCommitment />}
        {requiresSettlement && selectedStep === TradeActivationStep.settlement && (
          <TradeSettlement />
        )}
        {selectedStep === TradeActivationStep.activate && <TradeActivate />}
      </TabContent>
    </Space>
  )
}

export default TradeActivationLayout
